/**
 * A named-field token: a bare identifier, optionally followed by one format filter.
 *
 * Exported because the same pattern has to drive DETECTION (which tokens an author is shown,
 * and which must be linked before publishing) and SUBSTITUTION. Two copies would drift, and
 * the drift shows up as a token that was never offered for linking and renders as literal
 * `{{ ... }}` text in an executed deed. Callers must build their own instance when they need
 * the `g` flag — a shared global regex carries `lastIndex` between calls.
 *
 * The filter carries its ARGUMENTS (`currency: "GBP"`, `date: "%d %b %Y"`, `number: 2, 2`):
 * without them a money or date token matched nothing, was never offered for linking, and
 * Liquid then resolved the unbound name to an empty string — a blank in an executed deed.
 * The argument list stops at a `|` as well as at `}`, so `{{ a | b: 1 | c }}` still fails to
 * match and is reported as too many filters rather than read as one filter named `b: 1 | c`.
 */
/**
 * A name segment. Dots join them, so `investor.last_name` states the shape of the data it comes
 * from: the parent object, then the value. An analyst reading a deed can see the hierarchy
 * without a legend, which `investor_last_name` cannot show — there is no telling which
 * underscore was the boundary.
 */
const NAME_SEGMENT = String.raw`[A-Za-z_]\w*`;
const FIELD_NAME = String.raw`${NAME_SEGMENT}(?:\.${NAME_SEGMENT})*`;

export const NAMED_FIELD_TOKEN_PATTERN = String.raw`\{\{\s*(${FIELD_NAME})\s*(?:\|\s*([A-Za-z_]\w*(?:\s*:[^}|]*?)?)\s*)?\}\}`;

/**
 * A direct Liquid expression against the render root, which needs no linking.
 *
 * Kept apart from a field name now that a name may contain dots: without this, widening the
 * grammar made `{{ data.investor.firstName }}` read as a field called `data.investor.firstName`
 * and demand a link of its own — and generation refuses a document with an unlinked field, so
 * every template using a direct expression would have stopped generating.
 */
export const isDirectDataExpression = (name: string): boolean => /^data(\.|\[|$)/.test(name);

export const namedFieldTokenRegex = (): RegExp => new RegExp(NAMED_FIELD_TOKEN_PATTERN, 'g');

// Substitutes each named-field token `{{ name }}` with its bound Liquid
// expression, leaving direct `{{ data.* }}` and unbound names untouched. Single
// pass, no re-expansion: binding values only ever reference `data`.
export const expandFieldBindings = (
	text: string,
	fieldBindings: Record<string, string> | undefined
): string => {
	if (!fieldBindings) {
		return text;
	}
	return text.replace(
		namedFieldTokenRegex(),
		(match, name: string, filter: string | undefined) => {
			const bound = fieldBindings[name];
			if (bound === undefined) {
				return match;
			}
			if (filter === undefined) {
				return bound;
			}
			// `bound` is itself a `{{ … }}` token, so splice the filter inside its braces.
			const inner = bound.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/);
			return inner ? `{{ ${inner[1]} | ${filter} }}` : bound;
		}
	);
};

/**
 * A field name as a person reads it: `investor.first_name` → "Investor first name".
 *
 * The fallback label wherever a name has no data point behind it yet. Dots separate as
 * underscores do, because an author has no reason to know which of the two the model used.
 */
export const humaniseFieldName = (name: string): string => {
	const cleaned = name.replace(/[._]+/g, ' ').trim();
	return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

/**
 * Splits a catalogue path into its expression and its filters: `a.b | date: "%d"` → both parts.
 *
 * Quote-aware, so a `|` inside an argument (`join: " | "`) is an argument and not a filter
 * boundary. Shared because the editor derives a field's NAME from the expression alone — a
 * format baked into a name reads as `investor.birth_date_date_d_b_y` — and the preview has to
 * match a stored binding back to the data point it came from.
 */
export const splitFilters = (path: string): { expression: string; filters: string[] } => {
	const parts: string[] = [];
	let current = '';
	let quote: string | null = null;
	for (const character of path) {
		if (quote !== null) {
			current += character;
			if (character === quote) {
				quote = null;
			}
			continue;
		}
		if (character === '"' || character === "'") {
			quote = character;
			current += character;
			continue;
		}
		if (character === '|') {
			parts.push(current);
			current = '';
			continue;
		}
		current += character;
	}
	parts.push(current);
	const [expression = '', ...filters] = parts.map((part) => part.trim());
	return { expression, filters: filters.filter((filter) => filter !== '') };
};
